import { Log, Logger, WebStorageStateStore } from 'oidc-client-ts';

const databaseName = 'oidc-client-ts-auth-store';
const authStoreName = 'authStore';
const currentDatabaseVersion = 1;

// DEVELOPER NOTE: Use this for debugging issues with IndexedDB storage.
Log.setLogger(console);
Log.setLevel(Log.WARN);

export class IndexedDBAuthStore extends WebStorageStateStore {
  private readonly logger = new Logger('IndexedDBAuthStore');
  private readonly indexedDBReady: Promise<void>;
  private readonly keyPrefix = 'oidc.';

  private db: IDBDatabase | null = null;
  private newVersion: number | null = null;

  public constructor() {
    super();

    this.indexedDBReady = new Promise((resolve, reject) => {
      this.logger.debug(
        'Initializing IndexedDBAuthStore',
        databaseName,
        currentDatabaseVersion
      );

      const request = indexedDB.open(databaseName, currentDatabaseVersion);

      // https://developer.mozilla.org/en-US/docs/Web/API/IDBOpenDBRequest/upgradeneeded_event
      // https://w3c.github.io/IndexedDB/#dom-idbopendbrequest-onupgradeneeded
      request.onupgradeneeded = (event) => {
        this.logger.debug('onupgradeneeded event triggered', {
          oldVersion: event.oldVersion,
          newVersion: event.newVersion,
        });

        this.db = request.result;
        this.newVersion = event.newVersion;

        if (event.newVersion === 1) {
          this.logger.debug('Creating object store ', authStoreName);

          this.db.createObjectStore(authStoreName);

          this.logger.debug('Object store created successfully', authStoreName);
        }
      };

      request.onsuccess = () => {
        this.logger.debug('IndexedDB opened successfully');

        this.db = request.result;

        if (this.newVersion === 1) {
          // Migrate localStorage data into IndexedDB - this is to ensure uninterrupted use of session data.
          this.logger.debug(
            'Migrating localStorage data to IndexedDB for compatibility',
            this.newVersion
          );

          const migrationPromises = Object.entries(localStorage)
            .filter(([key]) => key.startsWith(this.keyPrefix))
            .map(([key, value]) => {
              this.logger.debug(
                'Migrating key from localStorage to IndexedDB',
                key,
                value
              );
              const unPrefixedKey = key.substring(this.keyPrefix.length);
              // Here we consciously use the setDirect method to avoid waiting for the indexedDBReady.
              // We avoid doing this because it will either cause a deadlock or some other issue.
              return this._setDirect(unPrefixedKey, value)
                .then(() => {
                  this.logger.debug(
                    'Migrated key from localStorage to IndexedDB',
                    key,
                    unPrefixedKey
                  );
                })
                .catch((err) => {
                  this.logger.error(
                    'Error migrating key from localStorage to IndexedDB',
                    key,
                    err
                  );
                });
            });

          Promise.all(migrationPromises)
            .then(() => {
              this.logger.debug('All localStorage keys migrated successfully');
            })
            .catch((err) => {
              this.logger.error('Error during localStorage migration', err);
            })
            .finally(() => {
              this.logger.debug(
                'Migration attempt finished, resolving indexedDBReady'
              );
              resolve();
            });
        } else {
          // No migration needed, resolve immediately
          resolve();
        }
      };

      request.onerror = (event) => {
        this.logger.error('Error opening IndexedDB', event);

        this.db = null;

        reject(['Error opening IndexedDB', event]);
      };
    });
  }

  /**
   * Sets a key directly in the IndexedDB store without waiting for the indexedDBReady promise.
   * THIS IS SAFE TO USE ONLY DURING A MIGRATION PROCESS.
   * DO NOT USE THIS FOR ANY OTHER PURPOSE, because it bypasses the indexedDBReady promise, which is for consumers of this module only.
   * @param key The key to set in the store.
   * @param value The value to set for the key.
   * @returns A promise that resolves when the key is set successfully, or rejects with an error if the operation fails.
   * @deprecated This method is intended for migration purposes only and should not be used in regular operations.
   * This deprecation isn't to say that this method is bad or unusable, it's ONLY to discourage devs from using it without clear purpose.
   */
  private _setDirect(key: string, value: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!this.db) {
        this.logger.error('Database is not initialized', databaseName);
        reject(new Error('Database is not initialized'));
        return;
      }
      const store = this.db
        .transaction(authStoreName, 'readwrite')
        .objectStore(authStoreName);

      const request = store.put(value, this.keyPrefix + key);

      request.onsuccess = () => {
        this.logger.debug('Key set successfully (direct)', key);
        // Optionally, also update localStorage for compatibility
        localStorage.setItem(this.keyPrefix + key, value);
        resolve();
      };

      request.onerror = (event) => {
        this.logger.error('Error setting key (direct)', key, event);
        reject(event);
      };
    });
  }

  private getAuthStore(): IDBObjectStore {
    this.logger.debug('Getting object store', authStoreName);

    if (!this.db) {
      this.logger.error('Database is not initialized', databaseName);
      throw new Error('Database is not initialized');
    }

    this.logger.debug('Opening transaction for authStore');

    return this.db
      .transaction(authStoreName, 'readwrite')
      .objectStore(authStoreName);
  }

  private runAuthStoreOperation<T>(
    operation: (authStore: IDBObjectStore) => Promise<T>
  ): Promise<T> {
    this.logger.debug('Preparing to run operation on authStore');

    return this.indexedDBReady.then(
      () =>
        new Promise<T>((resolve, reject) => {
          this.logger.debug('Running operation on authStore');

          const store = this.getAuthStore();

          operation(store)
            .then((result) => {
              this.logger.debug('Operation completed successfully');
              resolve(result);
            })
            .catch((error) => {
              this.logger.error('Error running operation on authStore', error);
              reject(error);
            });
        })
    );
  }

  set(key: string, value: string): Promise<void> {
    this.logger.debug('Preparing to set key with value', key, value);

    const setOperation = (authStore: IDBObjectStore) => {
      return new Promise<void>((resolve, reject) => {
        this.logger.debug('Setting key with value in IndexedDB', key, value);

        const request = authStore.put(value, this.keyPrefix + key);

        request.onsuccess = () => {
          this.logger.debug('Key set successfully', key);

          this.logger.debug(
            'Writing value into fallback localStorage for compatibility',
            key,
            value
          );

          localStorage.setItem(this.keyPrefix + key, value);

          this.logger.debug(
            'Value written into localStorage successfully',
            key,
            value
          );

          resolve();
        };

        request.onerror = (event) => {
          this.logger.error('Error setting key', key, event);
          reject(event);
        };
      });
    };

    return this.runAuthStoreOperation(setOperation);
  }

  get(key: string): Promise<string | null> {
    this.logger.debug('Preparing to get key', key);

    const getOperation = (store: IDBObjectStore) => {
      return new Promise<string | null>((resolve, reject) => {
        this.logger.debug('Getting key from IndexedDB', key);

        const request = store.get(this.keyPrefix + key);

        request.onsuccess = () => {
          const result = request.result;
          if (result !== undefined) {
            this.logger.debug('Key retrieved successfully', key);
            resolve(result);
          } else {
            this.logger.debug('Key not found', key);
            resolve(null);
          }
        };

        request.onerror = (event) => {
          this.logger.error('Error getting key', key, event);
          reject(event);
        };
      });
    };

    return this.runAuthStoreOperation(getOperation);
  }

  remove(key: string): Promise<string | null> {
    this.logger.debug('Preparing to remove key', key);

    const removeOperation = (authStore: IDBObjectStore) => {
      return new Promise<string | null>((resolve, reject) => {
        this.logger.debug('Removing key from IndexedDB', key);

        const request = authStore.delete(this.keyPrefix + key);

        request.onsuccess = () => {
          this.logger.debug('Key removed successfully', key);
          resolve(key);
        };

        request.onerror = (event) => {
          this.logger.error('Error removing key', key, event);
          reject(event);
        };
      });
    };

    return this.runAuthStoreOperation(removeOperation);
  }

  getAllKeys(): Promise<string[]> {
    this.logger.debug('Preparing to get all keys');

    const getAllKeysOperation = (authStore: IDBObjectStore) => {
      return new Promise<string[]>((resolve, reject) => {
        this.logger.debug('Retrieving all keys from IndexedDB');

        const request = authStore.getAllKeys();

        request.onsuccess = () => {
          const keys = request.result;

          this.logger.debug('Retrieved keys successfully', keys.length);

          resolve(
            keys.map((key) => {
              const keyStr = key.toString();

              return keyStr.startsWith(this.keyPrefix)
                ? keyStr.substring(this.keyPrefix.length)
                : keyStr;
            })
          );
        };

        request.onerror = (event) => {
          this.logger.error('Error getting all keys', event);
          reject(event);
        };
      });
    };

    return this.runAuthStoreOperation(getAllKeysOperation);
  }
}
